import React, { Component, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import omit from 'lodash/omit';

import { FormattedMessage } from '../../util/reactIntl';
import { parse } from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { makeGetListingsByIdSelector } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, ModalInMobile } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  initialValues,
  validUrlQueryParamsFromProps,
  getDerivedRenderData,
  getSearchPageResourceLocatorStringParams,
  onResetAll,
  createFilterValueChangeHandler,
  onSortBy,
} from './SearchPage.shared';
import { loadData, searchMapListingsForView, setActiveListing } from './SearchPage.duck';
import {
  setClientSearchPageSize,
  shouldRequestSearchPageSize,
} from './SearchResultsPanel/searchPageSize';

import FilterComponent from './FilterComponent';
import SearchMapClustered from './SearchMapClustered/SearchMapClustered';
import DeliveryFilter, { SHIPPING_PARAM, PICKUP_PARAM } from './DeliveryFilter/DeliveryFilter';
import SellersFilter, {
  SPONSORED_PARAM,
  RATED_PARAM,
  SELLER_PARAM,
} from './SellersFilter/SellersFilter';
import {
  filterListingsForRatedSellers,
  isSellerFilterActive,
} from './SellersFilter/sellersFilterHelpers';
import DateListedFilter, { LISTED_PARAM } from './DateListedFilter/DateListedFilter';
import { isListedFilterActive } from './DateListedFilter/dateListedFilterHelpers';
import ActiveFilterTags from './ActiveFilterTags/ActiveFilterTags';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import SearchPageAccessWrapper from './SearchPageAccessWrapper';
import SearchErrors from './SearchErrors';

import css from './SearchPage.module.css';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout
const EMPTY_LISTING_IDS = [];

const getViewportWidth = () =>
  typeof window !== 'undefined' ? window.innerWidth : MODAL_BREAKPOINT;

// Compare filter-relevant URL params for map refetch (ignore pagination + view toggle).
const mapFilterSearchKey = search => {
  const params = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const { page, view, ...filters } = params;
  return JSON.stringify(filters);
};

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      viewportWidth: getViewportWidth(),
    };

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);
    this.handleViewportResize = this.handleViewportResize.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);

    // Map view
    this.handleToggleMapView = this.handleToggleMapView.bind(this);
    this.fetchMapListings = this.fetchMapListings.bind(this);
    this.handleSearchPageSize = this.handleSearchPageSize.bind(this);
    this.pendingPageSize = null;
    this.reloadTimer = null;
  }

  componentDidMount() {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleViewportResize);
      window.addEventListener('orientationchange', this.handleViewportResize);
    }
    if (this.isMapView()) {
      this.fetchMapListings();
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleViewportResize);
      window.removeEventListener('orientationchange', this.handleViewportResize);
    }
    clearTimeout(this.reloadTimer);
  }

  pageSizeRequestState() {
    return {
      activePerPage: this.props.pagination?.perPage,
      inFlightPerPage: this.props.searchParams?.perPage,
      searchInProgress: this.props.searchInProgress,
      pendingPageSize: this.pendingPageSize,
    };
  }

  // Store the measured page size, then refetch only when that size is not
  // already showing or already requested. The timeout lets the route's own
  // loadData (mount / filter change) pick up the size first.
  handleSearchPageSize(nextSize) {
    if (!nextSize) {
      return;
    }
    setClientSearchPageSize(nextSize);
    if (!shouldRequestSearchPageSize(nextSize, this.pageSizeRequestState())) {
      if (Number(this.props.pagination?.perPage) === nextSize) {
        this.pendingPageSize = nextSize;
      }
      return;
    }
    this.pendingPageSize = nextSize;
    clearTimeout(this.reloadTimer);
    this.reloadTimer = setTimeout(() => {
      const stillNeeded = shouldRequestSearchPageSize(nextSize, {
        ...this.pageSizeRequestState(),
        pendingPageSize: null,
      });
      if (!stillNeeded) {
        return;
      }
      const { onReloadListings, params, location, config } = this.props;
      if (onReloadListings) {
        onReloadListings(params, location.search, config);
      }
    }, 0);
  }

  componentDidUpdate(prevProps) {
    const prevIsMapView = parse(prevProps.location.search).view === 'map';
    const isMapView = this.isMapView();
    const mapFiltersChanged =
      mapFilterSearchKey(prevProps.location.search) !==
      mapFilterSearchKey(this.props.location.search);

    // Refetch the map dataset when entering map view or when filter params change (not pagination).
    if (isMapView && (!prevIsMapView || mapFiltersChanged)) {
      this.fetchMapListings();
    }

    // A failed fill must not stick, or the next measurement of that same size is ignored.
    const searchFailed =
      prevProps.searchInProgress && !this.props.searchInProgress && this.props.searchListingsError;
    if (searchFailed) {
      this.pendingPageSize = null;
    }
  }

  handleViewportResize() {
    this.setState({ viewportWidth: window.innerWidth });
  }

  // Whether the page is currently showing the map view (?view=map)
  isMapView() {
    return parse(this.props.location.search).view === 'map';
  }

  // Fetch all pickup-enabled listings matching the current filters for the map.
  fetchMapListings() {
    const { onSearchMapListings, location, config, params = {} } = this.props;
    onSearchMapListings(location.search, params.listingType, config);
  }

  // Toggle between grid (list) and map view, preserving all other search params.
  handleToggleMapView(showMap) {
    const { history, routeConfiguration, location } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const queryParams = showMap ? { ...urlQueryParams, view: 'map' } : omit(urlQueryParams, 'view');

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );
    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config, location } = this.props;
    onResetAll({
      history,
      routeConfiguration,
      config,
      location,
      urlQueryParams: validUrlQueryParamsFromProps(this.props),
      setState: this.setState.bind(this),
    });
  }

  getHandleChangedValueFn(useHistoryPush) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;

    return createFilterValueChangeHandler(
      {
        history,
        routeConfiguration,
        config,
        location,
        currentPathParams,
        urlQueryParams: validUrlQueryParamsFromProps(this.props),
        setState: this.setState.bind(this),
        getState: () => this.state,
      },
      useHistoryPush
    );
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;
    onSortBy({
      history,
      routeConfiguration,
      location,
      urlQueryParams: validUrlQueryParamsFromProps(this.props),
      urlParam,
      values,
    });
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      intl,
      listings = [],
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      routeConfiguration,
      config,
      params: currentPathParams = {},
      currentUser,
      mapListings = [],
      mapListingsInProgress = false,
      mapListingsError = null,
      onActivateListing,
      activeSeller = null,
      sellerNotFound = false,
    } = this.props;

    const isMapView = this.isMapView();

    const {
      listingTypePathParam,
      sortConfig,
      validQueryParams,
      availableFilters,
      selectedFilters,
      isValidDatesFilter,
      selectedFiltersCountForMobile,
      totalItems,
      listingsAreLoaded,
      conflictingFilterActive,
      showCreateListingsLink,
      title,
      description,
      schema,
      marketplaceCurrency,
      listingCategories,
      listingFieldsConfig,
      defaultFiltersConfig,
    } = getDerivedRenderData({
      intl,
      location,
      config,
      routeConfiguration,
      searchParams,
      pagination,
      listings,
      searchInProgress,
      currentPathParams,
      currentUser,
    });

    // ── Shipping / Pickup delivery filters ──────────────────────────────────

    // validQueryParams (from getDerivedRenderData → pickSearchParamsOnly) calls
    // validFilterParams with dropNonFilterParams=true, which strips pub_pickupEnabled
    // because it's not a registered Console filter. Use validUrlQueryParamsFromProps
    // directly — it keeps all params (dropNonFilterParams=false).
    const fullUrlParams = validUrlQueryParamsFromProps(this.props);

    const handleDeliverySubmit = values => {
      this.getHandleChangedValueFn(true)(values);
    };

    const handlePickupRadiusSubmit = values => {
      this.getHandleChangedValueFn(true)(values);
    };

    const deliveryFilterJSX = (
      <DeliveryFilter
        key="deliveryFilter"
        id="SearchFiltersDesktop.delivery"
        className={css.filter}
        urlQueryParams={fullUrlParams}
        onSubmit={handleDeliverySubmit}
        onRadiusSubmit={handlePickupRadiusSubmit}
        getAriaLabel={lbl => `${lbl} filter`}
        containerId="SearchPageWithGrid_DesktopFilters"
      />
    );

    // ── Sellers filter (seller-type + rated) ─────────────────────────────────
    const sellersFilterJSX = (
      <SellersFilter
        key="sellersFilter"
        id="SearchFiltersDesktop.sellers"
        className={css.filter}
        urlQueryParams={fullUrlParams}
        activeSeller={activeSeller}
        sellerNotFound={sellerNotFound}
        onSubmit={this.getHandleChangedValueFn(true)}
        getAriaLabel={lbl => `${lbl} filter`}
        containerId="SearchPageWithGrid_DesktopFilters"
      />
    );

    // Date listed sits between Category and Price (handoff placement).
    const dateListedInsertAfterIndex = (() => {
      const categoryIndex = availableFilters.findIndex(f => f.schemaType === 'category');
      if (categoryIndex >= 0) {
        return categoryIndex;
      }
      const listingTypeIndex = availableFilters.findIndex(f => f.schemaType === 'listingType');
      return listingTypeIndex >= 0 ? listingTypeIndex : -1;
    })();

    const renderDateListedFilter = (idPrefix, containerId) => (
      <DateListedFilter
        key="dateListedFilter"
        id={`${idPrefix}.dateListed`}
        className={css.filter}
        urlQueryParams={fullUrlParams}
        onSubmit={this.getHandleChangedValueFn(true)}
        getAriaLabel={lbl => `${lbl} filter`}
        containerId={containerId}
      />
    );

    const renderConfiguredFilters = (idPrefix, containerId, filterComponentProps = {}) => {
      const nodes = [];
      availableFilters.forEach((filterConfig, index) => {
        const key = `${idPrefix}.${filterConfig.scope || 'built-in'}.${filterConfig.key}`;
        const filterId = `${idPrefix}.${filterConfig.key.toLowerCase()}`;
        nodes.push(
          <FilterComponent
            key={key}
            id={filterId}
            className={css.filter}
            config={filterConfig}
            containerId={containerId}
            listingCategories={listingCategories}
            marketplaceCurrency={marketplaceCurrency}
            urlQueryParams={validQueryParams}
            initialValues={initialValues(this.props, this.state.currentQueryParams)}
            getHandleChangedValueFn={this.getHandleChangedValueFn}
            intl={intl}
            liveEdit
            showAsPopup={false}
            {...filterComponentProps}
          />
        );
        if (index === dateListedInsertAfterIndex) {
          nodes.push(renderDateListedFilter(idPrefix, containerId));
        }
      });
      if (dateListedInsertAfterIndex < 0) {
        nodes.unshift(renderDateListedFilter(idPrefix, containerId));
      }
      return nodes;
    };
    // ────────────────────────────────────────────────────────────────────────

    const handleRemoveActiveFilterTag = removeParams => {
      this.getHandleChangedValueFn(true)(removeParams);
    };

    const activeFilterTagsJSX = (
      <ActiveFilterTags
        className={css.activeFilterTags}
        urlQueryParams={fullUrlParams}
        config={config}
        listingFieldsConfig={listingFieldsConfig}
        defaultFiltersConfig={defaultFiltersConfig}
        onRemove={handleRemoveActiveFilterTag}
      />
    );

    // Client-side safety net when server stamps are stale; server totalItems stays authoritative.
    const filteredListings = filterListingsForRatedSellers(listings, fullUrlParams);

    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          labelId={`${mode}-search-page-sort-by`}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
        showCreateListingsLink={showCreateListingsLink}
      />
    );

    // Grid / map view toggle
    const viewToggleJSX = (
      <div className={css.viewToggle} role="group">
        <button
          type="button"
          className={!isMapView ? css.viewToggleOptionSelected : css.viewToggleOption}
          aria-pressed={!isMapView}
          onClick={() => this.handleToggleMapView(false)}
        >
          <FormattedMessage id="SearchPage.viewAsList" />
        </button>
        <button
          type="button"
          className={isMapView ? css.viewToggleOptionSelected : css.viewToggleOption}
          aria-pressed={isMapView}
          onClick={() => this.handleToggleMapView(true)}
        >
          <FormattedMessage id="SearchPage.viewAsMap" />
        </button>
      </div>
    );

    // Map panel: inline below the sticky mobile toolbar (toggle stays visible), split view on desktop.
    const isMobileLayout = this.state.viewportWidth < MODAL_BREAKPOINT;

    const mapClusteredJSX = (
      <>
        <SearchErrors
          searchListingsError={mapListingsError}
          isValidDatesFilter={isValidDatesFilter}
        />
        <div
          className={classNames(css.mapClusteredInner, {
            [css.mapClusteredLoading]: mapListingsInProgress,
          })}
        >
          <SearchMapClustered listings={mapListings} onActivateListing={onActivateListing} />
        </div>
      </>
    );

    const mobileMapPanelJSX =
      isMapView && isMobileLayout ? (
        <div className={css.mapPanelMobile}>
          <div className={css.mapWrapperGrid} data-testid="searchMapClusteredContainer">
            {mapClusteredJSX}
          </div>
        </div>
      ) : null;

    const desktopMapPanelJSX =
      isMapView && !isMobileLayout ? (
        <ModalInMobile
          className={css.mapPanelGrid}
          id="SearchPageGrid_map"
          isModalOpenOnMobile={false}
          onClose={() => this.handleToggleMapView(false)}
          showAsModalMaxWidth={MODAL_BREAKPOINT}
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.mapWrapperGrid} data-testid="searchMapClusteredContainer">
            {mapClusteredJSX}
          </div>
        </ModalInMobile>
      ) : null;

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
        <div className={css.layoutWrapperContainer}>
          <aside className={css.layoutWrapperFilterColumn} data-testid="filterColumnAside">
            <div className={css.filterColumnContent}>
              <div className={css.filtersHeader}>
                <span className={css.filtersTitle}>
                  <FormattedMessage id="SearchFiltersMobile.filtersButtonLabel" />
                </span>
                <button className={css.filtersResetLink} onClick={e => this.handleResetAll(e)}>
                  <FormattedMessage id="SearchPage.resetAllFilters" />
                </button>
              </div>
              {activeFilterTagsJSX}
              {renderConfiguredFilters(
                'SearchFiltersDesktop',
                'SearchPageWithGrid_DesktopFilters',
                {
                  isDesktop: true,
                }
              )}
              {deliveryFilterJSX}
              {sellersFilterJSX}
            </div>
          </aside>

          <div id="main-content" className={css.layoutWrapperMain} role="main">
            <div className={css.searchResultContainer}>
              {viewToggleJSX}
              <SearchFiltersMobile
                className={css.searchFiltersMobileList}
                urlQueryParams={validQueryParams}
                sortByComponent={sortBy('mobile')}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                showAsModalMaxWidth={MODAL_BREAKPOINT}
                onManageDisableScrolling={onManageDisableScrolling}
                onOpenModal={this.onOpenMobileModal}
                onCloseModal={this.onCloseMobileModal}
                resetAll={this.resetAll}
                selectedFiltersCount={
                  selectedFiltersCountForMobile +
                  // Hardcoded filters (shipping, pickup, seller-type, rated, listed) are not in the
                  // Console filter registry so getDerivedRenderData doesn't count them.
                  [
                    fullUrlParams?.[SHIPPING_PARAM] === 'true' ||
                      fullUrlParams?.[SHIPPING_PARAM] === true,
                    fullUrlParams?.[PICKUP_PARAM] === 'true' ||
                      fullUrlParams?.[PICKUP_PARAM] === true,
                    !!fullUrlParams?.[SPONSORED_PARAM],
                    fullUrlParams?.[RATED_PARAM] === 'true' ||
                      fullUrlParams?.[RATED_PARAM] === true,
                    isSellerFilterActive(fullUrlParams?.[SELLER_PARAM]),
                    isListedFilterActive(fullUrlParams?.[LISTED_PARAM]),
                  ].filter(Boolean).length
                }
                activeFilterTags={activeFilterTagsJSX}
                isMapVariant={false}
                showMapViewToggle
                isMapViewActive={isMapView}
                onMapViewToggle={this.handleToggleMapView}
                noResultsInfo={noResultsInfo}
                location={location}
              >
                {renderConfiguredFilters('SearchFiltersMobile', 'SearchPage_MobileFilters')}
                {React.cloneElement(deliveryFilterJSX, {
                  containerId: 'SearchPage_MobileFilters',
                })}
                {React.cloneElement(sellersFilterJSX, {
                  id: 'SearchFiltersMobile.sellers',
                  containerId: 'SearchPage_MobileFilters',
                })}
              </SearchFiltersMobile>
              {mobileMapPanelJSX}
              <MainPanelHeader
                className={css.mainPanel}
                sortByComponent={sortBy('desktop')}
                isSortByActive={sortConfig.active}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                noResultsInfo={noResultsInfo}
              />
              <div
                className={classNames(css.listingsForGridVariant, {
                  [css.listingsHiddenInMapView]: isMapView,
                  [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                })}
              >
                <SearchErrors
                  searchListingsError={searchListingsError}
                  isValidDatesFilter={isValidDatesFilter}
                  sellerNotFound={sellerNotFound}
                />
                <SearchResultsPanel
                  className={css.searchListingsPanel}
                  listings={filteredListings}
                  pagination={listingsAreLoaded ? pagination : null}
                  search={parse(location.search)}
                  isMapVariant={false}
                  listingTypeParam={listingTypePathParam}
                  intl={intl}
                  onPageSize={this.handleSearchPageSize}
                />
              </div>
            </div>
          </div>
          {desktopMapPanelJSX}
        </div>
        <FooterContainer />
      </Page>
    );
  }
}

/**
 * SearchPage "container" (grid layout): selects Redux state and dispatch handlers, then passes the
 * same prop surface as before to `SearchPageComponent` via `SearchPageAccessWrapper`.
 *
 * @param {Object} props - Router / route props from `routeConfiguration.js` and `Routes.js`
 * @returns {JSX.Element}
 */
const SearchPage = props => {
  const dispatch = useDispatch();
  const selectListingsById = useMemo(makeGetListingsByIdSelector, []);
  const selectMapListingsById = useMemo(makeGetListingsByIdSelector, []);

  const currentUser = useSelector(state => state.user?.currentUser);
  const {
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    mapListingsInProgress,
    mapListingsError,
    activeSeller,
    sellerNotFound,
  } = useSelector(state => state.SearchPage);
  const listings = useSelector(state =>
    selectListingsById(state, state.SearchPage.currentPageResultIds)
  );
  const mapListings = useSelector(state =>
    selectMapListingsById(state, state.SearchPage.mapListingIds || EMPTY_LISTING_IDS)
  );
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );
  const onSearchMapListings = useCallback(
    (search, listingType, config) =>
      dispatch(searchMapListingsForView(search, listingType, config)),
    [dispatch]
  );
  const onActivateListing = useCallback(listingId => dispatch(setActiveListing(listingId)), [
    dispatch,
  ]);
  const onReloadListings = useCallback(
    (params, search, config) => dispatch(loadData(params, search, config)),
    [dispatch]
  );

  return (
    <SearchPageAccessWrapper
      {...props}
      PageComponent={SearchPageComponent}
      currentUser={currentUser}
      listings={listings}
      mapListings={mapListings}
      mapListingsInProgress={mapListingsInProgress}
      mapListingsError={mapListingsError}
      pagination={pagination}
      scrollingDisabled={scrollingDisabled}
      searchInProgress={searchInProgress}
      searchListingsError={searchListingsError}
      searchParams={searchParams}
      onManageDisableScrolling={onManageDisableScrolling}
      onSearchMapListings={onSearchMapListings}
      onActivateListing={onActivateListing}
      onReloadListings={onReloadListings}
      activeSeller={activeSeller}
      sellerNotFound={sellerNotFound}
    />
  );
};

export default SearchPage;
